import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import DropdownFilter from './DropdownFilter';
import './MoreFiltersPopover.css';

const PANEL_WIDTH = 340;
const VIEWPORT_MARGIN = 12;

// react-select portals its menu to <body>; those clicks must not close the popover.
const isInsideSelectMenu = (target) => (
  typeof target?.closest === 'function' &&
  Boolean(target.closest('.select__menu, .select__menu-portal'))
);

/**
 * Button + popover holding secondary quick filters. Each entry uses the same
 * shape as the inline filters in SearchFilters, so the controls look and behave
 * identically - they just live behind a button to keep the toolbar short.
 */
export default function MoreFiltersPopover({ filters = [], label = 'More filters' }) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0, width: PANEL_WIDTH });
  const containerRef = useRef(null);
  const panelRef = useRef(null);

  const activeCount = useMemo(
    () => filters.filter((filter) => {
      const value = filter?.value;
      if (Array.isArray(value)) return value.length > 0;
      return value != null && value !== '';
    }).length,
    [filters]
  );

  // The filters toolbar clips overflow, so the panel is portaled to <body> and
  // positioned against the button instead of nesting inside it.
  const updatePosition = useCallback(() => {
    const button = containerRef.current;
    if (!button) return;

    const rect = button.getBoundingClientRect();
    const width = Math.min(PANEL_WIDTH, window.innerWidth - VIEWPORT_MARGIN * 2);
    // Right-align with the button, then keep it on screen.
    const left = Math.min(
      Math.max(VIEWPORT_MARGIN, rect.right - width),
      window.innerWidth - width - VIEWPORT_MARGIN
    );

    setPosition({ top: rect.bottom + 8, left, width });
  }, []);

  useEffect(() => {
    if (!open) return undefined;

    updatePosition();

    const handleMouseDown = (event) => {
      if (containerRef.current?.contains(event.target)) return;
      if (panelRef.current?.contains(event.target)) return;
      if (isInsideSelectMenu(event.target)) return;
      setOpen(false);
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };

    // Follow the button when the page moves under it.
    const handleReflow = () => updatePosition();

    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', handleReflow);
    window.addEventListener('scroll', handleReflow, true);

    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', handleReflow);
      window.removeEventListener('scroll', handleReflow, true);
    };
  }, [open, updatePosition]);

  const handleClearAll = () => {
    filters.forEach((filter) => {
      if (filter?.isMulti) {
        filter?.onChange?.([]);
        return;
      }
      filter?.onChange?.(null);
    });
  };

  if (filters.length === 0) return null;

  return (
    <div className="more-filters" ref={containerRef}>
      <button
        type="button"
        className={`btn btn-outline-secondary more-filters-toggle ${open ? 'open' : ''} ${activeCount > 0 ? 'has-active' : ''}`}
        onClick={() => setOpen(prev => !prev)}
        aria-expanded={open}
        aria-haspopup="dialog"
        title={label}
      >
        <i className="bi bi-sliders"></i>
        <span className="more-filters-toggle-label">{label}</span>
        {activeCount > 0 && <span className="more-filters-badge">{activeCount}</span>}
      </button>

      {open && createPortal(
        <div
          ref={panelRef}
          className="more-filters-panel"
          role="dialog"
          aria-label={label}
          style={{ top: `${position.top}px`, left: `${position.left}px`, width: `${position.width}px` }}
        >
          <div className="more-filters-header">
            <span className="more-filters-title">Additional Filters</span>
            <button
              type="button"
              className="more-filters-clear"
              onClick={handleClearAll}
              disabled={activeCount === 0}
            >
              Clear all
            </button>
          </div>

          <div className="more-filters-list">
            {filters.map((filter, index) => (
              <div className="more-filters-item" key={filter.key || index}>
                <span className="more-filters-item-label">{filter.label || filter.placeholder}</span>
                <DropdownFilter
                  icon={filter.icon}
                  value={filter.value}
                  onChange={filter.onChange}
                  options={filter.options}
                  placeholder={filter.placeholder}
                  isClearable={filter.isClearable !== false}
                  isMulti={filter.isMulti === true}
                  showMultiSelectActions={filter.showMultiSelectActions !== false}
                  containerStyle={{ maxWidth: '100%', width: '100%' }}
                  // Must clear the popover, which sits above the page chrome.
                  selectStyles={{
                    menuPortal: (base) => ({ ...base, zIndex: 3000 }),
                    menu: (base) => ({ ...base, zIndex: 3000 })
                  }}
                />
              </div>
            ))}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
